import { DashboardHeader } from "@/components/layout/header";
import { chipShapeRaw, pressableRaw } from "@/components/overview/pressable";
import { ActionBar } from "@/components/triage/action-bar";
import { type SendState, StagedTray } from "@/components/triage/staged-tray";
import { TriageCard } from "@/components/triage/triage-card";
import {
  type Domain,
  type TriageAction,
  type TriageView,
  fetchTriage,
  sendTriageBatch,
} from "@/lib/api";
import { DOMAIN_LABEL, DOMAIN_ORDER, isoDay, relativeAge } from "@/lib/overview";
import {
  BUCKET_LABEL,
  type QueueFilter,
  type QueueOrder,
  STAGED_LABEL,
  type StageInput,
  type UndoEntry,
  allowedActions,
  applyStage,
  applyUndo,
  bucketCounts,
  buildBatch,
  buildQueue,
  filterFor,
  groupStageable,
  labelOf,
  makeStaged,
  needsPanel,
  stageError,
  undoLeft,
} from "@/lib/triage";
import {
  type StoredState,
  loadSent,
  loadStaged,
  newClientBatchId,
  saveSent,
  saveStaged,
} from "@/lib/triage-store";
import { Box, Spinner, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { useCallback, useEffect, useMemo, useState } from "react";

const POLL_MS = 60_000;
const chip = css(pressableRaw, chipShapeRaw);
const FILTERS: QueueFilter[] = [
  "todo",
  "likely-done",
  "stale",
  "unlabeled",
  "live",
  "waiting",
  "snoozed",
];
const KEYS: Record<string, TriageAction> = {
  d: "done",
  x: "drop",
  k: "keep",
  s: "snooze",
  r: "reschedule",
  n: "note",
};

const navButton = css(pressableRaw, {
  px: "10",
  py: "4",
  borderRadius: "4",
  _disabled: { opacity: "0.4", cursor: "not-allowed" },
});

// Gmail-style snackbar: one small line, bottom-left, dark, a text "Undo" and a dismiss ×.
const snackbar = css({
  fontFamily: "sans",
  position: "fixed",
  bottom: "16",
  left: "16",
  zIndex: "[1000]",
  display: "flex",
  alignItems: "center",
  gap: "4",
  maxW: "[calc(100vw - 32px)]",
  pl: "16",
  pr: "4",
  py: "4",
  borderRadius: "4",
  bg: "bg.neutral.bold",
  color: "text.inverse",
  boxShadow: "overlay",
  fontSize: "[14px]",
  lineHeight: "[20px]",
  whiteSpace: "nowrap",
});

const snackText = css({ mr: "8", overflow: "hidden", textOverflow: "ellipsis" });

const snackUndo = css({
  fontFamily: "sans",
  fontSize: "[14px]",
  fontWeight: "medium",
  color: "[#8ab4f8]",
  bg: "transparent",
  border: "none",
  borderRadius: "4",
  px: "8",
  py: "6",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "[rgba(255, 255, 255, 0.08)]" },
  _focusVisible: { bg: "[rgba(255, 255, 255, 0.12)]" },
});

const snackClose = css({
  fontSize: "[14px]",
  color: "text.inverse",
  opacity: "0.7",
  bg: "transparent",
  border: "none",
  borderRadius: "4",
  w: "[28px]",
  h: "[28px]",
  cursor: "pointer",
  outline: "none",
  _hover: { opacity: "1", bg: "[rgba(255, 255, 255, 0.08)]" },
  _focusVisible: { opacity: "1", bg: "[rgba(255, 255, 255, 0.12)]" },
});

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const UNDO_KEY = IS_MAC ? "⌘Z" : "Ctrl+Z";

function typingIn(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable
  );
}

export function TriagePage() {
  const today = isoDay(new Date());
  const [view, setView] = useState<TriageView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<QueueFilter>("todo");
  const [domain, setDomain] = useState<Domain | "all">("all");
  const [order, setOrder] = useState<QueueOrder>("priority");
  const [stored, setStored] = useState<StoredState>(loadStaged);
  const [sent, setSent] = useState(loadSent);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [mode, setMode] = useState<TriageAction | null>(null);
  const [sendState, setSendState] = useState<SendState>({ kind: "idle" });
  const [stageNote, setStageNote] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoEntry | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const v = await fetchTriage();
      setView(v);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load");
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Persist staging on every change. If storage is blocked, staging still works for this tab.
  useEffect(() => {
    saveStaged(stored);
  }, [stored]);
  useEffect(() => {
    saveSent(sent);
  }, [sent]);

  // Once bee lists a sent id as pending (or settled), the local "sent" mark is no longer needed.
  useEffect(() => {
    if (!view) return;
    const known = new Set([...view.pending, ...Object.keys(view.recent)]);
    const left = Object.fromEntries(Object.entries(sent.ids).filter(([id]) => !known.has(id)));
    if (Object.keys(left).length !== Object.keys(sent.ids).length)
      setSent({ version: 1, ids: left });
  }, [view, sent]);

  const sentIds = useMemo(() => Object.keys(sent.ids), [sent]);
  // A staged task leaves the queue and every count at once. Undo or unstage brings it back.
  const stagedIds = useMemo(() => Object.keys(stored.items), [stored]);
  const queue = useMemo(
    () =>
      view
        ? buildQueue(view, { filter, domain, today, sent: sentIds, staged: stagedIds, order })
        : [],
    [view, filter, domain, today, sentIds, stagedIds, order]
  );
  const counts = useMemo(
    () => (view ? bucketCounts(view, { domain, today, sent: sentIds, staged: stagedIds }) : null),
    [view, domain, today, sentIds, stagedIds]
  );
  const queueIndex = currentId ? queue.findIndex((c) => c.id === currentId) : -1;
  // A staged card opened from the tray (or brought back by undo) shows outside the queue.
  const pinned =
    view && currentId && queueIndex === -1 && stored.items[currentId]
      ? (view.candidates.find((c) => c.id === currentId) ?? null)
      : null;
  const index = Math.max(0, queueIndex);
  const card = pinned ?? queue[index] ?? null;
  const staged = useMemo(
    () => Object.values(stored.items).sort((a, b) => a.stagedAt.localeCompare(b.stagedAt)),
    [stored]
  );
  const groupable = useMemo(() => groupStageable(queue, today), [queue, today]);
  const undoMs = undoLeft(undo, now);

  // Keep the position when the queue changes under the current card.
  useEffect(() => {
    if (card && card.id !== currentId) setCurrentId(card.id);
  }, [card, currentId]);

  // The undo window: re-render while it runs, then close it.
  useEffect(() => {
    if (!undo) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (undoLeft(undo, t) === 0) setUndo(null);
    }, 200);
    return () => clearInterval(id);
  }, [undo]);

  const go = useCallback(
    (delta: number) => {
      const next = pinned
        ? queue[0]
        : queue[Math.min(queue.length - 1, Math.max(0, index + delta))];
      if (next) setCurrentId(next.id);
      setMode(null);
    },
    [queue, index, pinned]
  );

  /** The card to show after `ids` leave the queue: the next one, else the one before. */
  const nextAfter = useCallback(
    (ids: Set<string>): string | null => {
      if (pinned) return queue.find((c) => !ids.has(c.id))?.id ?? null;
      const after = queue.slice(index).find((c) => !ids.has(c.id));
      const before = [...queue.slice(0, index)].reverse().find((c) => !ids.has(c.id));
      return (after ?? before)?.id ?? null;
    },
    [queue, index, pinned]
  );

  const commit = useCallback(
    (entries: ReturnType<typeof makeStaged>[], label: string) => {
      const r = applyStage(stored.items, entries, label);
      const t = Date.now();
      setStored({ ...stored, items: r.items });
      setUndo({ ...r.undo, at: t });
      setNow(t);
      setSendState({ kind: "idle" });
      setCurrentId(nextAfter(new Set(entries.map((e) => e.taskId))));
      setMode(null);
    },
    [stored, nextAfter]
  );

  const stage = useCallback(
    (input: StageInput): string | null => {
      if (!card) return "No card.";
      const err = stageError(card, input);
      if (err) return err;
      setStageNote(null);
      commit([makeStaged(card, input)], STAGED_LABEL[input.action]);
      return null;
    },
    [card, commit]
  );

  const doUndo = useCallback(() => {
    if (!view || !undo || undoLeft(undo) === 0) return;
    setStored((s) => ({ ...s, items: applyUndo(s.items, undo) }));
    const target = view.candidates.find((c) => c.id === undo.ids[0]);
    if (target) {
      setFilter((f) => filterFor(target, today, f));
      if (domain !== "all" && target.domain !== domain) setDomain("all");
      setCurrentId(target.id);
    }
    setUndo(null);
    setMode(null);
    setStageNote(null);
  }, [view, undo, today, domain]);

  const unstage = useCallback((taskId: string) => {
    setStored((s) => {
      const items = { ...s.items };
      delete items[taskId];
      return { ...s, items };
    });
    setUndo(null);
  }, []);

  const stageGroup = () => {
    const at = new Date();
    commit(
      groupable.map((c) => makeStaged(c, { action: "done" }, at)),
      `Marked ${groupable.length} done`
    );
    setStageNote(`Staged ${groupable.length} as Done. Undo, or remove any of them in the tray.`);
  };

  // Keyboard: d x k s r n act on the card; arrows move; esc closes the panel;
  // Cmd+Z (Ctrl+Z off a Mac) undoes the last decision while its window is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = IS_MAC ? e.metaKey : e.ctrlKey;
      if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "z") {
        // A text field keeps its own undo.
        if (typingIn(e.target) || !undo) return;
        e.preventDefault();
        doUndo();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || typingIn(e.target)) {
        if (e.key === "Escape") setMode(null);
        return;
      }
      if (e.key === "Escape") return setMode(null);
      if (e.key === "ArrowRight") return go(1);
      if (e.key === "ArrowLeft") return go(-1);
      const action = KEYS[e.key.toLowerCase()];
      if (!action || !card || !allowedActions(card)[action]) return;
      e.preventDefault();
      if (needsPanel(card, action)) setMode(action);
      else stage({ action });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, go, stage, undo, doUndo]);

  const send = async () => {
    if (!view) return;
    const clientBatchId = stored.clientBatchId ?? newClientBatchId();
    if (!stored.clientBatchId) setStored((s) => ({ ...s, clientBatchId }));
    const batch = buildBatch(staged, view, clientBatchId);
    if (batch.decisions.length === 0) return;
    setSendState({ kind: "sending" });
    try {
      const res = await sendTriageBatch(batch);
      const sentNow = new Set(batch.decisions.map((d) => d.taskId));
      const at = new Date().toISOString();
      setStored((s) => {
        const items = Object.fromEntries(
          Object.entries(s.items).filter(([id]) => !sentNow.has(id))
        );
        return { version: 1, items };
      });
      setSent((s) => ({
        version: 1,
        ids: { ...s.ids, ...Object.fromEntries([...sentNow].map((id) => [id, at])) },
      }));
      setUndo(null);
      setSendState({
        kind: "sent",
        id: res.id,
        count: res.decisions,
        duplicate: res.duplicate,
        obsidianRunning: res.obsidianRunning,
      });
      load();
    } catch (err) {
      setSendState({
        kind: "error",
        message: err instanceof Error ? err.message : "unknown error",
      });
    }
  };

  return (
    <Box
      display="flex"
      flexDir="column"
      gap="24"
      pt="12"
      pb="20"
      px={{ base: "16", md: "20" }}
      maxW="[1400px]"
      mx="auto"
      minH="screen"
    >
      <DashboardHeader
        trailing={
          view ? (
            <Text textStyle="mono.xs" color="text.subtlest" hideBelow="md">
              bee read the vault {relativeAge(view.generatedAt)}
            </Text>
          ) : undefined
        }
      />

      <Box display="flex" flexDir="column" gap="6">
        <Text as="h1" fontSize="[20px]" fontWeight="semibold" color="text.bold" m="0">
          Triage
        </Text>
        <Text textStyle="mono.xs" color="text.subtlest">
          {view
            ? `${view.candidates.length} open tasks · ${view.candidates.filter((c) => labelOf(c)).length} labeled · ${
                view.labelsAt ? `labels from work ${relativeAge(view.labelsAt)}` : "no labels yet"
              }`
            : "loading…"}
        </Text>
        <Text fontSize="13" color="text.subtle">
          Decide one task at a time. Nothing changes until you send. Hal applies each decision only
          if the line still reads the same.
        </Text>
      </Box>

      {loadError && (
        <Text textStyle="mono.sm" color="text.danger">
          triage: {loadError}
        </Text>
      )}
      {!view && !loadError && (
        <Box display="flex" alignItems="center" gap="4">
          <Spinner size="sm" />
          <Text textStyle="mono.xs">Loading tasks…</Text>
        </Box>
      )}

      {view && counts && (
        <>
          <Box display="flex" gap="16" flexWrap="wrap" alignItems="center">
            <Box
              as="fieldset"
              display="flex"
              gap="6"
              flexWrap="wrap"
              border="none"
              p="0"
              m="0"
              minW="0"
              aria-label="Queue"
            >
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={chip}
                  aria-pressed={filter === f}
                  onClick={() => {
                    setFilter(f);
                    setMode(null);
                  }}
                >
                  {f === "todo" ? "To triage" : BUCKET_LABEL[f]} · {counts[f]}
                </button>
              ))}
            </Box>
            <Box
              as="fieldset"
              display="flex"
              gap="6"
              flexWrap="wrap"
              border="none"
              p="0"
              m="0"
              minW="0"
              aria-label="Domain"
            >
              {(
                ["all", ...DOMAIN_ORDER.filter((d) => d !== "personal")] as Array<Domain | "all">
              ).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={chip}
                  aria-pressed={domain === d}
                  onClick={() => setDomain(d)}
                >
                  {d === "all" ? "All" : DOMAIN_LABEL[d]}
                </button>
              ))}
            </Box>
            <button
              type="button"
              className={chip}
              aria-pressed={order === "oldest"}
              onClick={() => setOrder((o) => (o === "oldest" ? "priority" : "oldest"))}
              title="Sort the queue by added date, oldest first. Unknown dates go last."
            >
              oldest first
            </button>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{
              base: "[minmax(0, 1fr)]",
              lg: "[minmax(0, 2fr) minmax(0, 1fr)]",
            }}
            gap="32"
            alignItems="start"
          >
            <Box
              as="section"
              display="flex"
              flexDir="column"
              gap="16"
              minW="0"
              aria-label="Current task"
            >
              <Box display="flex" gap="8" alignItems="center" flexWrap="wrap">
                <button
                  type="button"
                  className={navButton}
                  onClick={() => go(-1)}
                  disabled={!pinned && index === 0}
                  aria-label="Previous task"
                >
                  ←
                </button>
                <Text textStyle="mono.xs" color="text.subtle">
                  {pinned
                    ? `staged · ${queue.length} left`
                    : queue.length
                      ? `${index + 1} of ${queue.length}`
                      : "0 of 0"}
                  {staged.length ? ` · ${staged.length} staged` : ""}
                </Text>
                <button
                  type="button"
                  className={navButton}
                  onClick={() => go(1)}
                  disabled={!pinned && index >= queue.length - 1}
                  aria-label="Next task"
                >
                  →
                </button>
                {groupable.length > 0 && (
                  <button
                    type="button"
                    className={chip}
                    onClick={stageGroup}
                    title="Strong likely-done cards where every piece of evidence has a link"
                  >
                    Stage all {groupable.length} strong as Done
                  </button>
                )}
              </Box>
              {stageNote && (
                <Text as="output" display="block" textStyle="mono.xs" color="text.selected">
                  {stageNote}
                </Text>
              )}

              {card ? (
                <Box
                  display="flex"
                  flexDir="column"
                  gap="20"
                  p={{ base: "16", md: "20" }}
                  border="default"
                  borderRadius="8"
                  bg="surface"
                >
                  <TriageCard
                    key={card.id}
                    card={card}
                    today={today}
                    labelsAt={view.labelsAt}
                    staged={stored.items[card.id]}
                    recent={view.recent[card.id]}
                  />
                  <ActionBar
                    key={`${card.id}:${stored.items[card.id]?.stagedAt ?? ""}`}
                    card={card}
                    today={today}
                    staged={stored.items[card.id]}
                    mode={mode}
                    setMode={setMode}
                    onStage={stage}
                    onUnstage={() => unstage(card.id)}
                  />
                  <Text textStyle="mono.xs" color="text.subtlest" hideBelow="md">
                    keys: d done · x drop · k keep · s snooze · r reschedule · n note · ← → move ·
                    esc close
                  </Text>
                </Box>
              ) : (
                <Text fontSize="13" color="text.subtle" p="20" border="default" borderRadius="8">
                  Nothing left in this queue.
                </Text>
              )}
            </Box>

            <StagedTray
              staged={staged}
              view={view}
              sendState={sendState}
              onSend={send}
              onRemove={unstage}
              onJump={(id) => {
                setCurrentId(id);
                setMode(null);
              }}
            />
          </Box>
          {undo && undoMs > 0 && (
            <output className={snackbar} aria-live="polite">
              <span className={snackText}>{undo.label}</span>
              <button
                type="button"
                className={snackUndo}
                onClick={doUndo}
                title={`Undo (${UNDO_KEY})`}
              >
                Undo
              </button>
              <button
                type="button"
                className={snackClose}
                onClick={() => setUndo(null)}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </output>
          )}
          {view.errors.length > 0 && (
            <Text textStyle="mono.xs" color="text.warning">
              bee reported {view.errors.length} read problem{view.errors.length === 1 ? "" : "s"}:{" "}
              {view.errors.map((e) => e.path).join(", ")}
            </Text>
          )}
        </>
      )}
    </Box>
  );
}
